import { FormInput, FormPhoneInput, FormSelect } from "@/components";
import { Button } from "@/components/ui";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetOwnershipAccount, useGetOwnershipGrid } from "@/services";
import { ArrowLeft, Building2, FileText, Lock, Mail, MapPin, Pencil, Phone, Save, Trash2, X } from "lucide-react";
import moment from "moment";
import { ReactNode, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CurrentTitleSection from "./CurrentTitleSection";
import OwnersTenantsSection from "./OwnersTenantsSection";
import {
  ADDRESS, ADDRESS_1, ADDRESS_2, CATEGORY, CITY, COUNTRY, EMERGENCY_CONTACTS, INSURANCE_TYPE, NO_OF_GARAGES, PIN,
  POC_EMAIL, POC_MOB_NO, POC_NAME, PROPERTY_BLOCK, PROPERTY_MODEL, PROPERTY_NAME, PROPERTY_SIZE, PROPERTY_ZONE, STATE,
  UNIT_NUMBER, ZIPCODE,
} from "./consts";

const PROPERTY_FORM_ID = "property-inline-form";

const Field = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <div className="text-sm text-gray-900 break-words mt-0.5">{value === undefined || value === null || value === "" ? "—" : value}</div>
  </div>
);

const SectionTitle = ({ icon, children }: { icon: ReactNode; children: string }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-2">{icon}{children}</h4>
);

const fmtDateTime = (v?: string) => (v ? moment(v).format("MM/DD/YYYY h:mm A") : undefined);
const SOURCE_LABELS: Record<string, string> = { PROPERTY: "Property", OWNER_PRIMARY: "Owner primary", CUSTOM: "Custom" };

interface PropertyDetailViewProps {
  property: any;
  onBack: () => void;
  /** useAddEditProperty() result: form, lists and save logic reused for the inline edit */
  edit: any;
}

/** Property detail (view mode) with in-place edit of hierarchy, address and emergency contacts. */
const PropertyDetailView = ({ property, onBack, edit }: PropertyDetailViewProps) => {
  const navigate = useNavigate();
  const communityId = useSelector((state: RootState) => state.currentData.communityData?.communityId);
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const isMasterData = location.pathname.toLowerCase().includes("master-data");
  const canEdit = location.pathname.startsWith("/admin") || !!masterDataPermission?.update;

  const {
    form, onSubmit, requiredFields, key, stateList, propertyZones, propertyBlock, insuranceType,
    propertyCategories, propertyModels, emergeCount, onAddMore, onRemove, lastInputRef,
    isUpdatePending, isInlineEditing, setIsInlineEditing, resetFromDetails,
  } = edit;
  const errors: any = form.formState.errors;
  const isEditing = !!isInlineEditing;
  const req = (name: string) => requiredFields?.includes(name);
  const options = (list: any, label: string, value: string) =>
    (list?.data || []).map((v: any) => ({ label: v?.[label], value: v?.[value]?.toString() }));

  // Current (active) title on this property, from the Property Ownership grid
  const request = useMemo(() => ({
    filters: [
      { field: "propertyId", value: property?.propertyId, operator: "EQUAL" },
      { field: "status", value: "ACTIVE", operator: "EQUAL" },
    ],
    sort: [{ field: "ownershipId", direction: "DESC" }],
    pageNumber: 0,
    pageSize: 1,
  }), [property?.propertyId]);
  const { data: ownershipGrid } = useGetOwnershipGrid({
    communityId,
    data: request,
    queryParams: { enabled: !!communityId && communityId != "all" && !!property?.propertyId && isMasterData },
  });
  const title = ownershipGrid?.data?.[0];
  // Full title record (owners, share, correspondence) for the Current Title section and side panel
  const { data: accountData } = useGetOwnershipAccount({ communityId, ownershipId: title?.ownershipId ? String(title.ownershipId) : "" });
  const account = accountData?.data;
  const openOwner = (userId: number) =>
    navigate(`${location.pathname.replace(/\/properties\/.*$/, "")}/${routes.owner}/${routes.details}/${userId}`);
  const openTitle = () => title && navigate(`${location.pathname.replace(/\/properties\/.*$/, "")}/${routes.propertyOwnership}/${title.ownershipId}`);

  const address = property?.address || {};
  const contacts = (property?.emergencyContacts || []).filter((c: any) => c?.pocName || c?.pocMobileNo || c?.pocEmailId);

  const cancelEdit = () => {
    resetFromDetails();
    setIsInlineEditing(false);
  };
  const saveCancel = (
    <div className="flex gap-2">
      <Button type="button" size="sm" variant="outline" onClick={cancelEdit} disabled={isUpdatePending}><X className="w-4 h-4" /> Cancel</Button>
      <Button type="submit" size="sm" form={PROPERTY_FORM_ID} disabled={isUpdatePending}><Save className="w-4 h-4" /> Save</Button>
    </div>
  );

  return (
    <FormProvider {...form}>
      {/* Fields are bound through react-hook-form, so they don't need to sit inside this element
          (the Current Title section has its own <form>; forms can't be nested). */}
      <form id={PROPERTY_FORM_ID} onSubmit={form.handleSubmit(onSubmit)} />
      <div className="p-4 md:p-6 flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="icon" onClick={onBack} aria-label="Back"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h2 className="text-lg md:text-xl font-semibold">Property Details</h2>
              <p className="text-sm text-gray-500">Property master record with hierarchy, address and current title</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border-t-4 border-primary shadow-sm grid grid-cols-1 xl:grid-cols-[1fr_300px]">
          <div className="p-4 md:p-6 flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4 border-b pb-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-primary shrink-0"><Building2 className="w-6 h-6" /></div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Property / Community</p>
                  <p className="text-lg font-semibold">{property?.name || "—"}</p>
                  <p className="text-sm text-gray-500">{property?.communityName}</p>
                </div>
              </div>
              <Field label="Property ID" value={property?.displayId || property?.propertyId} />
            </div>

            {title ? (
              <CurrentTitleSection title={title} account={account} communityId={communityId}
                canEdit={canEdit} onOpenTitle={openTitle} />
            ) : (
              <div className="flex flex-col gap-2">
                <SectionTitle icon={<FileText className="w-4 h-4" />}>Current Title</SectionTitle>
                <p className="text-sm text-gray-500">No active title on this property.</p>
              </div>
            )}

            {/* Hierarchy & Identification */}
            <div className="flex flex-col gap-4 border-t pt-4">
              <div className="flex items-center justify-between gap-2">
                <SectionTitle icon={<FileText className="w-4 h-4" />}>Hierarchy &amp; Identification</SectionTitle>
                {isEditing ? saveCancel : canEdit ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => setIsInlineEditing(true)}
                    title="Edit hierarchy, address and emergency contacts">
                    <Pencil className="w-4 h-4" /> Edit Property
                  </Button>
                ) : <></>}
              </div>
              {isEditing ? (
                <p className="text-xs text-gray-500 -mt-2">Editing Hierarchy &amp; Identification, Property Address and Emergency Contacts.</p>
              ) : <></>}
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormInput name={PROPERTY_NAME} control={form.control} label="Property Name" placeholder="Please enter"
                    error={errors[PROPERTY_NAME]?.message} isRequired={req(PROPERTY_NAME)} />
                  <FormSelect key={key?.propertyZones} name={PROPERTY_ZONE} control={form.control} label="Property Zone"
                    placeholder="Please select" error={errors[PROPERTY_ZONE]?.message} isRequired={req(PROPERTY_ZONE)}
                    options={options(propertyZones, "zoneName", "zoneId")} />
                  <FormSelect key={key?.propertyBlock} name={PROPERTY_BLOCK} control={form.control} label="Property Block"
                    placeholder="Please select" error={errors[PROPERTY_BLOCK]?.message} isRequired={req(PROPERTY_BLOCK)}
                    options={options(propertyBlock, "blockName", "blockId")} />
                  <FormInput name={UNIT_NUMBER} control={form.control} label="Unit Number" placeholder="Please enter"
                    error={errors[UNIT_NUMBER]?.message} isRequired={req(UNIT_NUMBER)} minLength={3} maxLength={10} />
                  <FormInput name={PIN} control={form.control} label="PIN" placeholder="Please enter"
                    error={errors[PIN]?.message} isRequired={req(PIN)} maxLength={15} />
                  <FormSelect key={key?.propertyCategories} name={CATEGORY} control={form.control} label="Property Category"
                    placeholder="Please select" error={errors[CATEGORY]?.message} isRequired={req(CATEGORY)}
                    options={options(propertyCategories, "categoryName", "categoryId")} />
                  <FormSelect key={key?.propertyModels} name={PROPERTY_MODEL} control={form.control} label="Property Model"
                    placeholder="Please select" error={errors[PROPERTY_MODEL]?.message} isRequired={req(PROPERTY_MODEL)}
                    options={options(propertyModels, "modelName", "modelId")} />
                  <FormInput name={PROPERTY_SIZE} control={form.control} label="Property Size (SFT)" placeholder="Please enter"
                    error={errors[PROPERTY_SIZE]?.message} isRequired={req(PROPERTY_SIZE)} variant="float" />
                  <FormInput name={NO_OF_GARAGES} control={form.control} label="Number of Garages" placeholder="Please enter"
                    error={errors[NO_OF_GARAGES]?.message} isRequired={req(NO_OF_GARAGES)} variant="integer" />
                  <FormSelect key={key?.insuranceType} name={INSURANCE_TYPE} control={form.control} label="Insurance Type"
                    placeholder="Please select" error={errors[INSURANCE_TYPE]?.message} isRequired={req(INSURANCE_TYPE)}
                    options={options(insuranceType, "insuranceType", "insuranceTypeId")} />
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Field label="Property Zone" value={property?.zoneName} />
                  <Field label="Property Block" value={property?.blockName} />
                  <Field label="Unit Number" value={property?.unitNumber} />
                  <Field label="PIN" value={property?.pin} />
                  <Field label="Property Category" value={property?.categoryName} />
                  <Field label="Property Model" value={property?.modelName} />
                  <Field label="Property Size (SFT)" value={property?.propertySizeSft != null ? Number(property.propertySizeSft).toLocaleString() : undefined} />
                  <Field label="Number of Garages" value={property?.garageCount} />
                  <Field label="Insurance Type" value={property?.insuranceType} />
                </div>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Community, zone, block and Property ID become non-editable once financial posting starts
              </p>
            </div>

            {/* Property Address */}
            <div className="flex flex-col gap-4 border-t pt-4">
              <SectionTitle icon={<MapPin className="w-4 h-4" />}>Property Address</SectionTitle>
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormInput name={`${ADDRESS}.${ADDRESS_1}`} control={form.control} label="Address Line 1" placeholder="Please enter"
                    error={errors[ADDRESS]?.[ADDRESS_1]?.message} isRequired={req(`${ADDRESS}.${ADDRESS_1}`)} />
                  <FormInput name={`${ADDRESS}.${ADDRESS_2}`} control={form.control} label="Address Line 2" placeholder="Please enter"
                    error={errors[ADDRESS]?.[ADDRESS_2]?.message} />
                  <FormInput name={`${ADDRESS}.${CITY}`} control={form.control} label="City" placeholder="Please enter"
                    error={errors[ADDRESS]?.[CITY]?.message} isRequired={req(`${ADDRESS}.${CITY}`)} variant="char" />
                  <FormSelect key={key?.stateKey} name={`${ADDRESS}.${STATE}`} control={form.control} label="State"
                    placeholder="Please select" error={errors[ADDRESS]?.[STATE]?.message} isRequired={req(`${ADDRESS}.${STATE}`)}
                    options={(stateList || []).map((s: any) => ({ label: s.name, value: s.code }))} />
                  <FormInput name={`${ADDRESS}.${ZIPCODE}`} control={form.control} label="ZIP Code" placeholder="XXXXX or XXXXX-XXXX"
                    error={errors[ADDRESS]?.[ZIPCODE]?.message} isRequired={req(`${ADDRESS}.${ZIPCODE}`)}
                    maxLength={10} variant="integerSpeChar" />
                  <FormInput name={`${ADDRESS}.${COUNTRY}`} control={form.control} label="Country" placeholder="Please enter"
                    error={errors[ADDRESS]?.[COUNTRY]?.message} isRequired={req(`${ADDRESS}.${COUNTRY}`)} />
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Field label="Address Line 1" value={address.addressLine1} />
                  <Field label="Address Line 2" value={address.addressLine2} />
                  <Field label="City" value={address.city} />
                  <Field label="State" value={address.state} />
                  <Field label="ZIP Code" value={address.zipCode} />
                  <Field label="Country" value={address.country} />
                </div>
              )}
            </div>

            {/* Emergency Contacts */}
            <div className="flex flex-col gap-4 border-t pt-4">
              <SectionTitle icon={<Phone className="w-4 h-4" />}>Emergency Contacts</SectionTitle>
              {isEditing ? (
                <div className="flex flex-col gap-3">
                  {Array(emergeCount).fill(0).map((_, index) => {
                    const named = form.watch(`${EMERGENCY_CONTACTS}.${index}.${POC_NAME}`);
                    const rowErrors = errors[EMERGENCY_CONTACTS]?.[index];
                    return (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-end bg-gray-50 rounded-lg p-3">
                        <FormInput ref={index == emergeCount - 1 ? lastInputRef : null}
                          name={`${EMERGENCY_CONTACTS}.${index}.${POC_NAME}`} control={form.control} label="POC Name" placeholder="Please enter" />
                        <FormPhoneInput name={`${EMERGENCY_CONTACTS}.${index}.${POC_MOB_NO}`} control={form.control} label="POC Mobile No."
                          placeholder="Please enter" isRequired={!!named} error={rowErrors?.[POC_MOB_NO]?.message} />
                        <FormInput name={`${EMERGENCY_CONTACTS}.${index}.${POC_EMAIL}`} control={form.control} label="POC Email"
                          placeholder="Please enter" isRequired={!!named} error={rowErrors?.[POC_EMAIL]?.message} />
                        {index != 0 ? (
                          <Button type="button" variant="ghost" onClick={() => onRemove(index)} aria-label="Remove contact"
                            className="p-0 rounded-full w-[35px] h-[35px] bg-red-100 hover:bg-red-200">
                            <Trash2 className="w-4 h-4 stroke-red-500" />
                          </Button>
                        ) : <div className="w-[35px]" />}
                      </div>
                    );
                  })}
                  <div>
                    <Button type="button" variant="ghost" onClick={onAddMore}
                      className="p-0 text-primary hover:bg-transparent hover:text-primary">+ Add More</Button>
                  </div>
                  <p className="text-xs text-gray-500">Rows without a POC name are not saved.</p>
                </div>
              ) : contacts.length === 0 ? <p className="text-sm text-gray-500">No emergency contacts.</p> : (
                <div className="flex flex-col gap-3">
                  {contacts.map((c: any, i: number) => (
                    <div key={c.emergencyContactId || i} className="grid grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-3">
                      <Field label="POC Name" value={c.pocName} />
                      <Field label="POC Mobile No." value={c.pocMobileNo} />
                      <Field label="POC Email" value={c.pocEmailId} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 xl:border-l p-4 md:p-6 flex flex-col gap-4 rounded-b-xl xl:rounded-bl-none xl:rounded-r-xl">
            <h3 className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Correspondence</h3>
            {account ? (
              <>
                <Field label="Address Source" value={SOURCE_LABELS[account.correspondenceAddressSource] || account.correspondenceAddressSource} />
                <Field label="Distribution" value={account.multiCorrespondence ? "All owners on this title" : "Primary contact only"} />
                <Field label="E-Communication" value={account.ecommSignedUp ? "Enrolled" : "Not enrolled"} />
                <Field label="Custom Address" value={account.customAddress
                  ? [account.customAddress.addressLine1, account.customAddress.addressLine2,
                     account.customAddress.pobox && `PO Box ${account.customAddress.pobox}`, account.customAddress.city,
                     account.customAddress.state, account.customAddress.zipCode].filter(Boolean).join(", ")
                  : "—"} />
                {/* Every E-Communication change (Edit Title) opens a new period; newest first */}
                <div className="flex flex-col gap-2 border-t pt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">E-Communication Audit Trail</p>
                  <div className="border rounded-lg bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-primary border-b">
                          <th className="px-3 py-2 font-semibold">Signed Up</th>
                          <th className="px-3 py-2 font-semibold">Effective From</th>
                          <th className="px-3 py-2 font-semibold">Effective To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(account.ecommHistory || []).map((h: any, i: number) => (
                          <tr key={i} className={i % 2 ? "bg-gray-50" : ""}>
                            <td className="px-3 py-2">{h.value === "YES" ? "Yes" : "No"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{h.effectiveFrom ? moment(h.effectiveFrom).format("MM/DD/YYYY") : "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{h.effectiveTo ? moment(h.effectiveTo).format("MM/DD/YYYY") : "Open-ended"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t pt-4 mt-auto">
                  <Field label="Created" value={fmtDateTime(account.createdAt)} />
                  <Field label="Last Updated" value={fmtDateTime(account.updatedAt)} />
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">{title ? "Loading…" : "No active title — correspondence is set on a title."}</p>
            )}
          </div>
        </div>

        {title && account ? (
          <OwnersTenantsSection account={account} communityId={communityId} canEdit={canEdit} onOpenOwner={openOwner} />
        ) : <></>}
      </div>
    </FormProvider>
  );
};

export default PropertyDetailView;
